import { computed, inject, Injector, Type } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';

import {
  SubmissionAdminSignalState,
  AdminWorkspaceSubTypeV2Schema,
} from '../../types';
import { SubmissionAdminService } from '../../services/submission-admin.service';
import {
  SUBMISSION_ADMIN_INITIAL_STATE,
  SUBMISSION_ADMIN_INITIAL_STATE_TOKEN,
} from '../../constants/submission-admin.constant';
import { SubmissionService } from '@data-access/configurations';

/**
 * Signal store for managing submission admin state including workspace sub-types search and filters
 */
export const submissionAdminSignalStore = signalStore(
  withState(() => inject(SUBMISSION_ADMIN_INITIAL_STATE_TOKEN)),
  withProps(
    (store, _submissionAdminService = inject(SubmissionAdminService)) => ({
      _injector: inject(Injector),
      _submissionAdminService,
      _submissionService: inject(SubmissionService),
      store,
    }),
  ),
  withComputed(({ store }) => ({
    $workspaceSubTypes: computed(() => store.workspaceSubTypes()),
    $totalWorkspaceSubTypes: computed(() => store.totalWorkspaceSubTypes()),
    $selectedWorkspaceSubType: computed(() => store.selectedWorkspaceSubType()),
    $currentFilters: computed(() => store.currentFilters()),
    $isLoading: computed(() => store.isLoading()),
    $error: computed(() => store.error()),
    $hasFilters: computed(() => {
      const filters = store.currentFilters();
      return !!filters.query || (filters.filters && filters.filters.length > 0);
    }),
    $canLoadMore: computed(() => {
      const total = store.totalWorkspaceSubTypes();
      const currentCount = store.workspaceSubTypes().length;
      return currentCount < total;
    }),
    $irisRoutingGroups: computed(() => store.irisRoutingGroupData.data()),
    $shortNames: computed(() => store.shortNames()),
  })),
  withMethods(({ store, _submissionAdminService, _submissionService }) => {
    /**
     * Load workspace sub types with HttpParams from wcgTableUtils
     */
    const _loadWorkspaceSubTypes = rxMethod<{
      queryParams: HttpParams;
      append?: boolean;
    }>(
      pipe(
        tap(({ append }) => {
          patchState(store, {
            error: null,
            isLoading: true,
            // Clear existing data if not appending
            ...(!append && { workspaceSubTypes: [] }),
          });
        }),
        switchMap(({ queryParams, append }) => {
          return _submissionAdminService
            .listWorkspaceSubTypes(queryParams)
            .pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error, isLoading: false }),
                finalize: () => patchState(store, { isLoading: false }),
                next: (response) => {
                  const existingSubTypes = append
                    ? store.workspaceSubTypes()
                    : [];
                  patchState(store, {
                    isLoading: false,
                    workspaceSubTypes: [...existingSubTypes, ...response.data],
                    totalWorkspaceSubTypes: response.total,
                    shortNames: response.short_names,
                    currentFilters: {
                      ...store.currentFilters(),
                      page: +(queryParams.get('page') || '1'),
                      page_size: +(queryParams.get('page_size') || '10'),
                      query: queryParams.get('query') || '',
                    },
                  });
                },
              }),
            );
        }),
      ),
    );

    /**
     * Load a specific workspace type by ID
     */
    const _loadWorkspaceTypeById = rxMethod<{
      workspaceTypeId: string;
      entityType?: string;
    }>(
      pipe(
        tap(() => {
          patchState(store, {
            isLoading: true,
            error: null,
          });
        }),
        switchMap(({ workspaceTypeId, entityType }) =>
          _submissionAdminService
            .getWorkspaceTypeById(workspaceTypeId, entityType)
            .pipe(
              tapResponse({
                next: (workspaceType: AdminWorkspaceSubTypeV2Schema) => {
                  patchState(store, {
                    selectedWorkspaceSubType: workspaceType,
                    isLoading: false,
                  });
                },
                error: (error: Error) => {
                  patchState(store, {
                    error,
                    isLoading: false,
                    selectedWorkspaceSubType: null,
                  });
                },
              }),
            ),
        ),
      ),
    );
    const _getIrisRoutingGroups = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap(() =>
          _submissionService.getIrisRoutingGroups().pipe(
            tapResponse({
              error: (error: Error) => patchState(store, { error: error }),
              finalize: () => patchState(store, { isLoading: false }),
              next: (routingGroups) =>
                patchState(store, { irisRoutingGroupData: routingGroups }),
            }),
          ),
        ),
      ),
    );

    return {
      /**
       * Load workspace sub types with HttpParams
       * This should be called from the component with params generated by wcgTableUtils.generateTableQueryParams()
       */
      loadWorkspaceSubTypes: (queryParams: HttpParams) =>
        _loadWorkspaceSubTypes({ queryParams, append: false }),

      /**
       * Load a specific workspace type by ID
       */
      loadWorkspaceTypeById: (workspaceTypeId: string, entityType?: string) =>
        _loadWorkspaceTypeById({ workspaceTypeId, entityType }),

      /**
       * Select a workspace sub type
       */
      selectWorkspaceSubType: (subType: AdminWorkspaceSubTypeV2Schema | null) =>
        patchState(store, { selectedWorkspaceSubType: subType }),

      /**
       * Clear current error
       */
      clearError: () => patchState(store, { error: null }),

      /**
       * Clear selection
       */
      clearSelection: () =>
        patchState(store, { selectedWorkspaceSubType: null }),

      /**
       * Reset to initial state
       */
      reset: () => patchState(store, { ...SUBMISSION_ADMIN_INITIAL_STATE }),

      loadIrisRoutingGroups: () => _getIrisRoutingGroups(),
    };
  }),
) satisfies Type<SubmissionAdminSignalState>;
